import { useState, useEffect } from "react";
import { useLocation, Navigate } from "react-router-dom";
import { AppConfig } from "@/config";
import { Button } from "@/components/ui/button";
import { useAuthStore } from "@/store/auth-store";

import { LogoSmall, DealersText, GoogleIcon, AnnouncementIcon } from "@/assets";

const LoginPage: React.FC = () => {
  const { isAuthenticated, isLoading, error } = useAuthStore();
  const location = useLocation();

  // Simple background loading state
  const [backgroundLoaded, setBackgroundLoaded] = useState(false);

  // Get the intended destination
  const from = location.state?.from?.pathname || "/";

  // Preload background image
  useEffect(() => {
    const img = new Image();
    img.onload = () => setBackgroundLoaded(true);
    img.src = "/login-bg.avif";
  }, []);

  const googleLogin = () => {
    const clientId = AppConfig.googleClientId;
    const redirectUri = encodeURIComponent(
      `${AppConfig.baseUrl}/auth/callback`
    );

    const googleAuthUrl =
      `https://accounts.google.com/o/oauth2/v2/auth?` +
      `redirect_uri=${redirectUri}&` +
      `prompt=consent&` +
      `response_type=code&` +
      `client_id=${clientId}&` +
      `scope=${encodeURIComponent("openid email profile")}&` +
      `access_type=offline`;

    window.location.href = googleAuthUrl;
  };

  if (isAuthenticated) {
    return <Navigate to={from} replace />;
  }

  // Dynamic background styles
  const backgroundStyles = {
    width: "100vw",
    height: "100dvh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background:
      "linear-gradient(135deg, #000000 0%, #1a1a1a 30%, #2d2d2d 70%, #404040 100%)",
    // Add image overlay when loaded
    ...(backgroundLoaded && {
      backgroundImage: `linear-gradient(135deg, rgba(0, 0, 0, 0.7) 0%, rgba(26, 26, 26, 0.6) 30%, 
      rgba(45, 45, 45, 0.5) 70%, rgba(64, 64, 64, 0.4) 100%), url('/login-bg.avif')`,
      backgroundSize: "cover",
      backgroundPosition: "center",
      backgroundRepeat: "no-repeat",
    }),
    transition: "background-image 1s ease",
  };

  return (
    <div className="relative">
      <div style={backgroundStyles}>
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/90 from-45% to-blue-900/20" />

        {/* Main content */}
        <div className="flex flex-col justify-between h-full z-50 py-10 px-5 m-10">
          <div className="flex flex-col items-center lg:flex-row lg:flex-1 gap-16">
            {/* Announcements */}
            <div className="flex-[0.7] container lg:-mt-64">
              <div className="max-w-[92%] bg-gray-800 text-white rounded-xl shadow-md p-6 mb-8">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-lg font-semibold">Announcement</h3>
                  <AnnouncementIcon className="w-7 h-7" />
                </div>
                <p className="text-sm">
                  Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed
                  do eiusmod tempor incididunt ut labore et dolore magna aliqua.
                  Ut enim ad minim veniam, quis nostrud exercitation ullamco
                  laboris nisi ut aliquip ex ea commodo consequat.
                </p>
              </div>
              <div className="max-w-[92%] bg-[#0284c780] text-white rounded-xl shadow-md p-6 ms-auto">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-lg font-semibold">Announcement</h3>
                  <AnnouncementIcon className="w-7 h-7" />
                </div>
                <p className="text-sm">
                  Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed
                  do eiusmod tempor incididunt ut labore et dolore magna aliqua.
                  Ut enim ad minim veniam, quis nostrud exercitation ullamco
                  laboris nisi ut aliquip ex ea commodo consequat.
                </p>
              </div>
            </div>

            {/* Login form */}
            <div className="flex-[0.5] flex flex-col items-center lg:-mb-32">
              <div className="flex items-center gap-1 mb-1">
                <LogoSmall />
                <p className="text-xs text-slate-100 font-light">
                  Performance Auto Group
                </p>
              </div>
              <DealersText className="mt-1 mb-6" />

              <div className="flex flex-col justify-center items-center bg-gray-100 p-8 rounded-lg shadow-md max-w-md min-w-96 mx-auto">
                <h2 className="text-xl font-bold text-gray-800 mb-1">
                  Welcome back!
                </h2>
                <p className="text-sm text-slate-600 mb-6">
                  Login with your Google account
                </p>

                {error && (
                  <div className="text-red-500 text-sm mb-4">
                    {error.message}
                  </div>
                )}

                <Button onClick={googleLogin} disabled={isLoading}>
                  <GoogleIcon
                    className="flex-shrink-0"
                    style={{ width: "1.2rem", height: "1.2rem" }}
                  />
                  {isLoading ? "Logging in..." : "Login with Google"}
                </Button>
              </div>
            </div>
          </div>

          {/* Footer quote */}
          <div className="w-full md:max-w-[40%]">
            <p className="text-sm text-slate-100 font-medium">
              "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do
              eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut
              enim ad minim"
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
